/**
 * Internal dependencies
 */
import type { Cart, CartItem, CartState } from '../../types/cart.types';
import { ACTION_TYPES as types } from './action-types';

// Loose action type — specific fields are narrowed with `as` casts in each
// case branch. This matches the pattern used in the admin store reducers.
type AnyAction = { [ key: string ]: unknown; type: string };

const defaultState: CartState = {
	cartData: {
		city: null,
		event_date: null,
		formattedSubtotal: '$0.00',
		formattedTotal: '$0.00',
		items: [],
		itemsCount: 0,
		state: null,
		subtotal: 0,
		total: 0,
	},
	error: null,
	isLoaded: false,
	isLoading: false,
	itemsPendingDelete: [],
	itemsPendingQuantity: [],
	warnings: [],
};

const reducer = ( state: CartState = defaultState, action: AnyAction ): CartState => {
	switch ( action.type ) {
		case types.ITEM_PENDING_DELETE: {
			const itemId = action.itemId as number;
			const isPending = action.isPending as boolean;
			const itemsPendingDelete = isPending
				? [ ...state.itemsPendingDelete, itemId ]
				: state.itemsPendingDelete.filter( ( id ) => id !== itemId );
			return { ...state, itemsPendingDelete };
		}

		case types.ITEM_PENDING_QUANTITY: {
			const itemId = action.itemId as number;
			const isPending = action.isPending as boolean;
			const itemsPendingQuantity = isPending
				? [ ...state.itemsPendingQuantity, itemId ]
				: state.itemsPendingQuantity.filter( ( id ) => id !== itemId );
			return { ...state, itemsPendingQuantity };
		}

		case types.REMOVE_CART_ITEM: {
			const itemId = action.itemId as number;
			const updatedItems = state.cartData.items.filter( ( item ) => item.id !== itemId );
			const subtotalCents = updatedItems.reduce( ( sum, item ) => sum + item.price * item.quantity, 0 );
			const itemsCount = updatedItems.reduce( ( sum, item ) => sum + item.quantity, 0 );
			return {
				...state,
				cartData: {
					...state.cartData,
					formattedSubtotal: `$${ ( subtotalCents / 100 ).toFixed( 2 ) }`,
					formattedTotal: `$${ ( subtotalCents / 100 ).toFixed( 2 ) }`,
					items: updatedItems,
					itemsCount,
					subtotal: subtotalCents,
					total: subtotalCents,
				},
			};
		}

		case types.SET_CART_DATA: {
			const cart = action.cart as Cart;
			return {
				...state,
				cartData: cart,
				isLoaded: true,
				isLoading: false,
				warnings: cart.warnings ?? [],
			};
		}

		case types.SET_ERROR: {
			return { ...state, error: action.error as null | string, isLoading: false };
		}

		case types.SET_LOADING: {
			return { ...state, isLoading: action.isLoading as boolean };
		}

		case types.UPDATE_CART_ITEM: {
			const itemId = action.itemId as number;
			const updates = action.updates as Partial< CartItem >;
			const updatedItems = state.cartData.items.map( ( item ) =>
				item.id === itemId ? { ...item, ...updates } : item,
			);
			const subtotalCents = updatedItems.reduce( ( sum, item ) => sum + item.price * item.quantity, 0 );
			const itemsCount = updatedItems.reduce( ( sum, item ) => sum + item.quantity, 0 );
			return {
				...state,
				cartData: {
					...state.cartData,
					formattedSubtotal: `$${ ( subtotalCents / 100 ).toFixed( 2 ) }`,
					formattedTotal: `$${ ( subtotalCents / 100 ).toFixed( 2 ) }`,
					items: updatedItems,
					itemsCount,
					subtotal: subtotalCents,
					total: subtotalCents,
				},
			};
		}

		default: {
			return state;
		}
	}
};

export default reducer;
